import type { LiteraMessage, LiteraRole } from "./litera-api";

/** Gør vilkårlige værdier til sikker streng — undgår render-crash på objekter fra API/storage. */
export function coerceString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (value == null) return fallback;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

export function chapterWordCount(content: unknown): number {
  return coerceString(content).split(/\s+/).filter(Boolean).length;
}

export function safeJsonParseArray<T>(raw: string | null | undefined, fallback: T[] = []): T[] {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function generateLiteraMessageId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `litera-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
  }
}

export function createLiteraMessage(
  role: LiteraRole,
  content: string,
  id?: string,
): LiteraMessage {
  return {
    id: id?.trim() || generateLiteraMessageId(),
    role,
    content: coerceString(content),
  };
}

export function normalizeLiteraMessage(raw: unknown): LiteraMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const message = raw as { id?: unknown; role?: unknown; content?: unknown };
  if (message.role !== "user" && message.role !== "assistant") return null;
  const content = coerceString(message.content);
  const id =
    typeof message.id === "string" && message.id.trim()
      ? message.id.trim()
      : generateLiteraMessageId();
  return {
    id,
    role: message.role,
    content,
  };
}

export function normalizeLiteraMessages(raw: unknown): LiteraMessage[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeLiteraMessage).filter((msg): msg is LiteraMessage => Boolean(msg));
}

export type ChapterRecord = {
  id: number;
  title: string;
  content: string;
  status: "DRAFT" | "IN_PROGRESS" | "DONE";
  sourceDocumentId?: string;
  sourceDocumentName?: string;
};

export function normalizeChapters(chapters: unknown): ChapterRecord[] {
  const source = Array.isArray(chapters) && chapters.length
    ? chapters
    : [{ id: 1, title: "Chapter 1", content: "", status: "DRAFT" }];
  return source.map((chapter, index) => {
    const row = chapter && typeof chapter === "object" ? chapter as Record<string, unknown> : {};
    const status = row.status === "IN_PROGRESS" || row.status === "DONE" ? row.status : "DRAFT";
    const id = typeof row.id === "number" && Number.isFinite(row.id) ? row.id : index + 1;
    const title = coerceString(row.title).trim() || `Chapter ${index + 1}`;
    return {
      ...(row as ChapterRecord),
      id,
      title,
      content: coerceString(row.content),
      status,
    };
  });
}

export function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(0, index), length - 1);
}

/** Parse AI JSON safely (strips fences, returns null on failure). */
export function parseAiJson(raw: unknown): unknown {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  const text = coerceString(raw).replace(/```json|```/gi, "").trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** String list from AI — handles ["a","b"] or [{question:"..."}] without render crashes. */
export function normalizeStringList(value: unknown, max = 10): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          const row = item as Record<string, unknown>;
          return coerceString(row.question ?? row.text ?? row.q ?? row.prompt ?? row.title).trim();
        }
        return coerceString(item).trim();
      })
      .filter(Boolean)
      .slice(0, max);
  }
  if (value && typeof value === "object") {
    const row = value as Record<string, unknown>;
    const nested = row.questions ?? row.items ?? row.clarifications ?? row.clarifyingQuestions;
    if (nested != null) return normalizeStringList(nested, max);
  }
  return [];
}

export type ProofSuggestion = {
  original: string;
  suggestion: string;
  explanation: string;
  type: "spelling" | "grammar" | "punctuation";
};

export function normalizeProofSuggestions(value: unknown): ProofSuggestion[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => {
      const row = item && typeof item === "object" ? item as Record<string, unknown> : {};
      const type: ProofSuggestion["type"] = row.type === "spelling" || row.type === "grammar" || row.type === "punctuation"
        ? row.type
        : "grammar";
      return {
        original: coerceString(row.original ?? row.source ?? row.before),
        suggestion: coerceString(row.suggestion ?? row.replacement ?? row.corrected ?? row.after),
        explanation: coerceString(row.explanation ?? row.reason ?? row.note),
        type,
      } satisfies ProofSuggestion;
    })
    .filter(item => item.original || item.suggestion)
    .slice(0, 12);
}

export type StyleScore = {
  score: number;
  strengths: string[];
  improvements: Array<{ issue: string; suggestion: string }>;
  readability: { level: string; grade: string };
};

export type ProofClarificationPayload = {
  questionAnswers: Array<{ question: string; answer: string }>;
  additionalInstructions?: string;
  answeredCount: number;
  totalQuestions: number;
};

export function buildProofClarifications(
  questions: unknown[],
  answers: unknown[],
  additionalInstructions = "",
): ProofClarificationPayload {
  const questionAnswers = questions.map((question, index) => ({
    question: coerceString(question).trim(),
    answer: coerceString(answers[index]).trim(),
  })).filter(item => item.question);
  const answeredCount = questionAnswers.filter(item => item.answer).length;
  const extra = coerceString(additionalInstructions).trim();
  return {
    questionAnswers,
    additionalInstructions: extra || undefined,
    answeredCount,
    totalQuestions: questionAnswers.length,
  };
}

export function normalizeStyleScore(value: unknown, fallback: StyleScore): StyleScore {
  if (!value || typeof value !== "object") return fallback;
  const row = value as Record<string, unknown>;
  const rawScore = typeof row.score === "number" ? row.score : Number(row.score);
  const score = Number.isFinite(rawScore) ? Math.min(100, Math.max(0, rawScore)) : fallback.score;
  const strengths = normalizeStringList(row.strengths, 6);
  const improvements = Array.isArray(row.improvements)
    ? row.improvements
      .map(item => {
        const imp = item && typeof item === "object" ? item as Record<string, unknown> : {};
        return {
          issue: coerceString(imp.issue ?? imp.problem ?? imp.title),
          suggestion: coerceString(imp.suggestion ?? imp.fix ?? imp.recommendation),
        };
      })
      .filter(imp => imp.issue || imp.suggestion)
      .slice(0, 8)
    : fallback.improvements;
  const readabilityRaw = row.readability && typeof row.readability === "object"
    ? row.readability as Record<string, unknown>
    : {};
  return {
    score,
    strengths: strengths.length ? strengths : fallback.strengths,
    improvements: improvements.length ? improvements : fallback.improvements,
    readability: {
      level: coerceString(readabilityRaw.level ?? readabilityRaw.label) || fallback.readability.level,
      grade: coerceString(readabilityRaw.grade ?? readabilityRaw.readingGrade) || fallback.readability.grade,
    },
  };
}
